import { useCallback, useEffect, useState, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  ElementFormatType,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, HeadingTagType } from '@lexical/rich-text';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $createImageNode } from './ImageNode';

interface BasicToolbarProps {
  showImageUpload?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
}

export function BasicToolbar({ showImageUpload = false, onImageUpload }: BasicToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update toolbar state based on current selection
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format states
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update block type and alignment
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        const type = element.getType();
        if (type === 'heading') {
          const tag = (element as any).__tag;
          setBlockType(tag);
        } else if ($isListNode(element)) {
          const parentList = element;
          const listType = (parentList as any).__listType;
          setBlockType(listType === 'number' ? 'ol' : 'ul');
        } else {
          setBlockType('paragraph');
        }
        
        // Get element format (alignment)
        const format = (element as any).__format || 0;
        if (format === 1) setElementFormat('left');
        else if (format === 2) setElementFormat('center');
        else if (format === 3) setElementFormat('right');
        else if (format === 4) setElementFormat('justify');
        else setElementFormat('left');
      }
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  // Format text commands
  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const formatUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  };

  const formatStrikethrough = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  };

  const formatCode = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
  };

  // Alignment commands
  const formatAlignment = (alignment: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
  };

  // List commands
  const formatBulletList = () => {
    if (blockType === 'ul') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType === 'ol') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  // Heading commands
  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };

  // Link command
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    }
  };

  // Image upload command
  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onImageUpload) return;

    try {
      const imageUrl = await onImageUpload(file);
      
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const imageNode = $createImageNode({
            src: imageUrl,
            altText: file.name,
            width: '100%',
          });
          selection.insertNodes([imageNode]);
        }
      });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  return (
    <div className="rte-toolbar">
      <button
        type="button"
        onClick={formatBold}
        className={`rte-toolbar-btn ${isBold ? 'active' : ''}`}
        aria-label="Format Bold"
        title="Bold"
      >
        <strong>B</strong>
      </button>
      
      <button
        type="button"
        onClick={formatItalic}
        className={`rte-toolbar-btn ${isItalic ? 'active' : ''}`}
        aria-label="Format Italic"
        title="Italic"
      >
        <em>I</em>
      </button>
      
      <button
        type="button"
        onClick={formatUnderline}
        className={`rte-toolbar-btn ${isUnderline ? 'active' : ''}`}
        aria-label="Format Underline"
        title="Underline"
      >
        <u>U</u>
      </button>

      <button
        type="button"
        onClick={formatStrikethrough}
        className={`rte-toolbar-btn ${isStrikethrough ? 'active' : ''}`}
        aria-label="Format Strikethrough"
        title="Strikethrough"
      >
        <s>S</s>
      </button>

      <button
        type="button"
        onClick={formatCode}
        className={`rte-toolbar-btn ${isCode ? 'active' : ''}`}
        aria-label="Format Code"
        title="Code"
      >
        {'</>'}
      </button>

      <span className="rte-toolbar-divider" />

      <button
        type="button"
        onClick={() => formatHeading('h1')}
        className={`rte-toolbar-btn ${blockType === 'h1' ? 'active' : ''}`}
        aria-label="Heading 1"
        title="Heading 1"
      >
        H1
      </button>

      <button
        type="button"
        onClick={() => formatHeading('h2')}
        className={`rte-toolbar-btn ${blockType === 'h2' ? 'active' : ''}`}
        aria-label="Heading 2"
        title="Heading 2"
      >
        H2
      </button>

      <button
        type="button"
        onClick={() => formatHeading('h3')}
        className={`rte-toolbar-btn ${blockType === 'h3' ? 'active' : ''}`}
        aria-label="Heading 3"
        title="Heading 3"
      >
        H3
      </button>

      <span className="rte-toolbar-divider" />

      <button
        type="button"
        onClick={formatBulletList}
        className={`rte-toolbar-btn ${blockType === 'ul' ? 'active' : ''}`}
        aria-label="Bullet List"
        title="Bullet List"
      >
        • List
      </button>

      <button
        type="button"
        onClick={formatNumberedList}
        className={`rte-toolbar-btn ${blockType === 'ol' ? 'active' : ''}`}
        aria-label="Numbered List"
        title="Numbered List"
      >
        1. List
      </button>

      <span className="rte-toolbar-divider" />

      <button
        type="button"
        onClick={insertLink}
        className="rte-toolbar-btn"
        aria-label="Insert Link"
        title="Insert Link"
      >
        🔗 Link
      </button>

      {showImageUpload && onImageUpload && (
        <>
          <button
            type="button"
            onClick={handleImageUploadClick}
            className="rte-toolbar-btn"
            aria-label="Upload Image"
            title="Upload Image"
          >
            🖼️ Image
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileChange}
            style={{ display: 'none' }}
          />
        </>
      )}

      <span className="rte-toolbar-divider" />

      <button
        type="button"
        onClick={() => formatAlignment('left')}
        className={`rte-toolbar-btn ${elementFormat === 'left' ? 'active' : ''}`}
        aria-label="Align Left"
        title="Align Left"
      >
        ⬅
      </button>

      <button
        type="button"
        onClick={() => formatAlignment('center')}
        className={`rte-toolbar-btn ${elementFormat === 'center' ? 'active' : ''}`}
        aria-label="Align Center"
        title="Align Center"
      >
        ↔
      </button>

      <button
        type="button"
        onClick={() => formatAlignment('right')}
        className={`rte-toolbar-btn ${elementFormat === 'right' ? 'active' : ''}`}
        aria-label="Align Right"
        title="Align Right"
      >
        ➡
      </button>

      <button
        type="button"
        onClick={() => formatAlignment('justify')}
        className={`rte-toolbar-btn ${elementFormat === 'justify' ? 'active' : ''}`}
        aria-label="Justify"
        title="Justify"
      >
        ⬌
      </button>
    </div>
  );
}
